import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { ArrowLeft, LogOut, Search, Users, User, Calendar } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL.endsWith('/api') ? BACKEND_URL : `${BACKEND_URL}/api`;

const AdminOffresEnCours = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [offers, setOffers] = useState([]);
  const [filteredOffers, setFilteredOffers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOffers();
  }, []);

  useEffect(() => {
    filterOffers();
  }, [offers, searchTerm]);

  const fetchOffers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/job-offers`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        // Admin voit seulement les offres de sa société avec statut 'en_cours'
        const adminOffers = data.filter(offer => 
          offer.company === user?.company && offer.status === 'en_cours'
        );
        setOffers(adminOffers);
      } else {
        toast.error('Erreur lors du chargement des offres');
      }
    } catch (error) {
      console.error('Error fetching offers:', error);
      toast.error('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  const filterOffers = () => {
    if (!searchTerm.trim()) {
      setFilteredOffers(offers);
    } else {
      const filtered = offers.filter(offer =>
        offer.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        offer.created_by_name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredOffers(filtered);
    }
  };

  const getCandidatesCount = (offer) => {
    return offer.candidatures ? offer.candidatures.length : 0;
  };

  const handleOfferClick = (offerId) => {
    navigate(`/admin/offer-detail/${offerId}`);
  };

  const handleLogout = () => {
    logout();
    toast.success('Déconnexion réussie');
  };

  // Company logos
  const companyLogos = {
    'Ma Conciergerie': 'https://customer-assets.emergentagent.com/job_a2d904ae-21ae-408b-b651-ff0f492712a1/artifacts/mruq8p60_PNG_MA%20CONCIERGERIE.png',
    'Mon Evenementiel': 'https://customer-assets.emergentagent.com/job_a2d904ae-21ae-408b-b651-ff0f492712a1/artifacts/o94kh96a_logo-monevenemenciel_png.png',
    'SISENS': 'https://customer-assets.emergentagent.com/job_a2d904ae-21ae-408b-b651-ff0f492712a1/artifacts/gwqu631h_25.04.10%20SISENS%20LOGO.png',
    'Horizon Solution': 'https://customer-assets.emergentagent.com/job_a2d904ae-21ae-408b-b651-ff0f492712a1/artifacts/md0676vy_Design%20sans%20titre%20%282%29.png',
    'Prysme': 'https://customer-assets.emergentagent.com/job_a2d904ae-21ae-408b-b651-ff0f492712a1/artifacts/nzxlkuw3_logo-prysme-couleur.png'
  };

  return (
    <div className="admin-offers-page">
      {/* Header */}
      <header className="admin-header">
        <div className="flex items-center space-x-4">
          <Button
            onClick={() => navigate('/admin')}
            variant="outline"
            size="sm"
            className="btn-outline"
            style={{ borderColor: '#eb5d2d', color: '#eb5d2d' }}
          >
            <ArrowLeft size={16} className="mr-2" />
            Retour
          </Button>
          <img
            src={companyLogos[user?.company]}
            alt={`Logo ${user?.company}`}
            className="admin-header-logo"
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
        </div>
        <div className="admin-header-center">
          <h1>Offres en cours - {user?.company}</h1>
          <p className="admin-header-subtitle">Consultation des offres d'emploi actives</p>
        </div>
        <Button
          onClick={handleLogout}
          variant="outline"
          size="sm"
          className="btn-outline"
          style={{ borderColor: '#eb5d2d', color: '#eb5d2d' }}
        >
          <LogOut size={16} className="mr-2" />
          Déconnexion
        </Button>
      </header>

      {/* Main Content */}
      <main className="admin-main">
        <div className="admin-offers-container">
          {/* Search Bar */}
          <div className="admin-search-section">
            <div className="admin-search-bar">
              <Search className="admin-search-icon" />
              <input
                type="text"
                placeholder="Rechercher par intitulé ou manager..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="admin-search-input"
              />
            </div>
          </div>

          {/* Offers Header */}
          <div className="admin-offers-header">
            <h2>Offres en cours ({filteredOffers.length})</h2>
          </div>

          {/* Offers Grid */}
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
            </div>
          ) : filteredOffers.length === 0 ? (
            <div className="admin-empty-state">
              <p>Aucune offre en cours trouvée</p>
            </div>
          ) : (
            <div className="admin-offers-grid">
              {filteredOffers.map((offer) => (
                <Card
                  key={offer.id}
                  className="admin-offer-card cursor-pointer transition-all duration-200 transform hover:scale-105"
                  onClick={() => handleOfferClick(offer.id)}
                >
                  <CardContent className="admin-offer-card-content">
                    <div className="admin-offer-header">
                      <h3 className="admin-offer-title">{offer.title}</h3>
                      <div className="admin-offer-badge admin-offer-badge-en-cours">
                        EN COURS
                      </div>
                    </div>
                    
                    <div className="admin-offer-info">
                      <div className="admin-offer-info-item">
                        <User className="w-4 h-4 text-gray-500" />
                        <span className="admin-offer-manager">
                          Manager: {offer.created_by_name || 'N/A'}
                        </span>
                      </div>
                      
                      <div className="admin-offer-info-item">
                        <Users className="w-4 h-4 text-gray-500" />
                        <span className="admin-offer-candidates">
                          {getCandidatesCount(offer)} candidature{getCandidatesCount(offer) !== 1 ? 's' : ''}
                        </span>
                      </div>

                      <div className="admin-offer-info-item">
                        <Calendar className="w-4 h-4 text-gray-500" />
                        <span className="admin-offer-date">
                          Créée le {new Date(offer.created_at).toLocaleDateString('fr-FR')}
                        </span>
                      </div>
                    </div>

                    <div className="admin-offer-description">
                      {offer.description && offer.description.length > 100
                        ? `${offer.description.substring(0, 100)}...`
                        : offer.description || 'Aucune description'}
                    </div>

                    <div className="admin-offer-footer">
                      <span className="admin-offer-company">{offer.company}</span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default AdminOffresEnCours;